/**
 * Filter helpers for Assignment → Time Off history.
 */

import {isInHistoryYear} from './historyYears'
import type {TimeOffHistoryEntry, TimeOffHistoryFilters} from './types'

const parseIso = (iso: string): Date => {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, (month || 1) - 1, day || 1)
}

const isWithinDateRange = (
  entry: TimeOffHistoryEntry,
  range: TimeOffHistoryFilters['dateRange'],
  today = new Date(),
): boolean => {
  if (range === 'all') return true
  const eventDate = parseIso(entry.dateIso)
  if (range === 'last30') {
    const cutoff = new Date(today)
    cutoff.setDate(cutoff.getDate() - 30)
    return eventDate >= cutoff
  }
  // Year to date
  const startOfYear = new Date(today.getFullYear(), 0, 1)
  return eventDate >= startOfYear
}

const matchesSearch = (entry: TimeOffHistoryEntry, search: string): boolean => {
  const query = search.trim().toLowerCase()
  if (!query) return true
  const formattedPolicy = formatActivityPolicy(entry.policyLabel, entry.typeName).toLowerCase()
  return (
    entry.activity.toLowerCase().includes(query) ||
    entry.policyLabel.toLowerCase().includes(query) ||
    formattedPolicy.includes(query) ||
    entry.typeName.toLowerCase().includes(query) ||
    entry.status.toLowerCase().includes(query) ||
    (entry.approvedBy ?? '').toLowerCase().includes(query) ||
    (entry.approvedDate ?? '').toLowerCase().includes(query)
  )
}

/**
 * Formats a policy label for clean display in history rows.
 * Strips raw internal request IDs (e.g. "Request 6ac568996d37870d1b141866 · DA Sick leave Policy"
 * → "DA Sick leave Policy", or "Request PTO-1048 · Standard Vacation" → "Standard Vacation").
 */
export const formatActivityPolicy = (
  policyLabel: string | undefined | null,
  typeName?: string,
): string => {
  if (!policyLabel) return typeName || ''
  const trimmed = policyLabel.trim()
  // Pattern: "Request <id> · <PolicyName>" (where <id> is an ObjectId hex, PTO ref, etc.)
  const matchWithPolicy = /^Request\s+\S+\s*·\s*(.+)$/i.exec(trimmed)
  if (matchWithPolicy?.[1]) {
    return matchWithPolicy[1].trim()
  }
  // Pattern: "Request <id>" without policy name attached
  const matchRequestOnly = /^Request\s+\S+\s*$/i.exec(trimmed)
  if (matchRequestOnly) {
    return typeName || 'Request'
  }
  return trimmed
}

/**
 * Apply tab, type, date, status, and free-text search to history rows.
 *
 * The date filter is `filters.year` when it is set (STFFLG-4310, flag timeOffHistoryYears: a year
 * or All, anchored on `hireDateIso` by `isInHistoryYear`, so rows before the hire date fall under
 * the hire year), else the legacy `filters.dateRange` relative to `today`.
 */
export const filterTimeOffHistory = (
  history: TimeOffHistoryEntry[],
  filters: TimeOffHistoryFilters,
  today = new Date(),
  hireDateIso?: string,
): TimeOffHistoryEntry[] =>
  history.filter((entry) => {
    if (filters.tab !== 'All' && !entry.categories.includes(filters.tab)) {
      return false
    }
    if (filters.typeName !== 'all' && entry.typeName !== filters.typeName) {
      return false
    }
    if (filters.status !== 'all' && entry.status !== filters.status) {
      return false
    }
    if (
      filters.year !== undefined
        ? !isInHistoryYear(entry.dateIso, filters.year, hireDateIso)
        : !isWithinDateRange(entry, filters.dateRange, today)
    ) {
      return false
    }
    return matchesSearch(entry, filters.search)
  })

export const formatSignedHours = (value: number | null, sign: 'used' | 'earned'): string => {
  if (value === null || value === undefined) return '—'
  const formatted = Number.isInteger(value) ? String(value.toFixed(2)) : value.toFixed(2)
  // Normalize trailing zeros: 8.00 → 8.00 kept for ledger consistency with mock
  return sign === 'used' ? `-${formatted}` : `+${formatted}`
}

export const formatBalanceHours = (value: number | null): string => {
  if (value === null || value === undefined) return '—'
  return value.toFixed(2)
}

const REQUEST_REF_IN_LABEL = /^Request\s+(\S+)/i

export interface RequestStatusLookup {
  id: string
  status: string
  externalRequestId?: string
  startDateIso?: string
  typeName?: string
}

export const getHistoryEntryRequestKey = (
  entry: TimeOffHistoryEntry,
  requests?: RequestStatusLookup[],
): {key: string | null; matchedRequest?: RequestStatusLookup} => {
  const labelRef = REQUEST_REF_IN_LABEL.exec(entry.policyLabel || '')?.[1]
  const explicitRef = entry.requestRef

  if (requests && requests.length > 0) {
    // An explicit reference always wins, whichever position its request has in the list.
    const matchesRef = (ref: string | undefined) => (r: RequestStatusLookup) =>
      Boolean(ref) && (r.externalRequestId === ref || r.id === ref)
    const byRef = requests.find(matchesRef(explicitRef)) ?? requests.find(matchesRef(labelRef))
    if (byRef) {
      return {key: byRef.id, matchedRequest: byRef}
    }

    // Fallback for request rows that carry no reference: same start day and type, but only when
    // exactly one request fits. Two same-day requests of one type stay separate rows, and ledger
    // rows (accruals, adjustments) never use the fallback, so they can't merge into a request.
    if (!explicitRef && !labelRef && entry.dateIso && entry.categories.includes('Requests')) {
      const sameDayAndType = requests.filter(
        (r) => r.startDateIso?.startsWith(entry.dateIso) && r.typeName === entry.typeName,
      )
      if (sameDayAndType.length === 1) {
        const [only] = sameDayAndType
        return {key: only.id, matchedRequest: only}
      }
    }
  }

  const key = explicitRef ?? labelRef ?? null
  return {key}
}

/**
 * Deduplicate time off history rows so that:
 * - Exactly one record is shown per request if the request was approved, denied, or cancelled.
 * - Prior pending submission records are suppressed once a decision is made.
 * - Requests still in 'Pending' status retain their pending record.
 * - Non-request ledger entries (accruals, manual adjustments, carryover) remain untouched.
 */
export const deduplicateTimeOffHistory = (
  history: TimeOffHistoryEntry[],
  requests?: RequestStatusLookup[],
): TimeOffHistoryEntry[] => {
  if (!history || history.length === 0) return []

  const requestRowsByKey = new Map<
    string,
    Array<{entry: TimeOffHistoryEntry; matchedRequest?: RequestStatusLookup}>
  >()
  const independentRows: TimeOffHistoryEntry[] = []

  for (const entry of history) {
    const {key, matchedRequest} = getHistoryEntryRequestKey(entry, requests)
    if (!key) {
      independentRows.push(entry)
    } else {
      const existing = requestRowsByKey.get(key)
      if (existing) {
        existing.push({entry, matchedRequest})
      } else {
        requestRowsByKey.set(key, [{entry, matchedRequest}])
      }
    }
  }

  const dedupedRequestRows: TimeOffHistoryEntry[] = []

  for (const [key, candidateRows] of requestRowsByKey.entries()) {
    if (candidateRows.length === 1) {
      dedupedRequestRows.push(candidateRows[0].entry)
      continue
    }

    const matchedRequest =
      candidateRows.find((c) => c.matchedRequest)?.matchedRequest ??
      requests?.find((r) => r.id === key || r.externalRequestId === key)
    const targetStatus = matchedRequest?.status

    if (targetStatus && targetStatus !== 'Pending') {
      // Exclude pending submission records
      const nonPending = candidateRows.filter(
        ({entry}) =>
          entry.status !== 'Pending' && !entry.activity.toLowerCase().includes('submitted'),
      )
      const candidates = nonPending.length > 0 ? nonPending : candidateRows

      const matchingStatus = candidates.filter(({entry}) => entry.status === targetStatus)
      if (matchingStatus.length > 0) {
        const preferred =
          matchingStatus.find(({entry}) => entry.usedHours !== null)?.entry ??
          matchingStatus[0].entry
        dedupedRequestRows.push(preferred)
      } else {
        const preferred =
          candidates.find(({entry}) => entry.usedHours !== null)?.entry ?? candidates[0].entry
        dedupedRequestRows.push(preferred)
      }
    } else if (targetStatus === 'Pending') {
      const pendingRow =
        candidateRows.find(({entry}) => entry.status === 'Pending')?.entry ?? candidateRows[0].entry
      dedupedRequestRows.push(pendingRow)
    } else {
      const hasDecided = candidateRows.some(
        ({entry}) =>
          entry.status === 'Approved' || entry.status === 'Denied' || entry.status === 'Cancelled',
      )
      if (hasDecided) {
        const nonPending = candidateRows.filter(
          ({entry}) =>
            entry.status !== 'Pending' && !entry.activity.toLowerCase().includes('submitted'),
        )
        const candidates = nonPending.length > 0 ? nonPending : candidateRows
        const preferred =
          candidates.find(({entry}) => entry.usedHours !== null)?.entry ?? candidates[0].entry
        dedupedRequestRows.push(preferred)
      } else {
        dedupedRequestRows.push(candidateRows[0].entry)
      }
    }
  }

  const combined = [...independentRows, ...dedupedRequestRows]
  combined.sort((a, b) => (b.dateIso || '').localeCompare(a.dateIso || ''))
  return combined
}

/**
 * Splits a balance card's policy line into a label and the value the card bolds:
 * "Current cap: 48 hrs" → {label: 'Current cap:', value: '48 hrs'};
 * "Accrues 3.08 hrs every 2 weeks" → {label: 'Accrues', value: '3.08 hrs every 2 weeks'}.
 * A line with neither shape stays all label.
 */
export function splitPolicyMetaLine(line: string): {label: string; value?: string} {
  const colon = line.indexOf(': ')
  if (colon > 0) {
    return {label: line.slice(0, colon + 1), value: line.slice(colon + 2)}
  }
  const accrues = /^(Accrues)\s+(.+)$/.exec(line)
  if (accrues) {
    return {label: accrues[1], value: accrues[2]}
  }
  return {label: line}
}

const ANNUAL_USAGE_LIMIT_LABEL = 'Annual usage limit:'

/**
 * The meta line for a Flexible or Manual policy's annual usage limit (STFFLG-4313), worded as the
 * msp's `annualUsageLimitLine` words it: the limit in hours rounded to at most 2 decimals with no
 * trailing zeros ("Annual usage limit: 40 hrs", "… 7.5 hrs"), or "Annual usage limit: None" when
 * the policy has no limit.
 */
export function annualUsageLimitLine(limitHours: number | null): string {
  if (limitHours === null) {
    return `${ANNUAL_USAGE_LIMIT_LABEL} None`
  }
  return `${ANNUAL_USAGE_LIMIT_LABEL} ${Number(limitHours.toFixed(2))} hrs`
}

/**
 * The policy lines under a balance card's divider, shared by the admin Assignment → Time Off card
 * and the candidate My Info → Time Off card (STFFLG-4313):
 * - An exact repeat of an earlier line is dropped. An msp before STFFLG-4313 sent a Manual
 *   policy's summary twice ("Balances managed individually" on two rows).
 * - A card that tracks no balance (a Flexible or Manual policy) shows its annual usage limit once:
 *   the msp's own "Annual usage limit:" line when it sends one, else `annualUsageLimitLine`, so
 *   the limit shows on an older msp too and never twice on a current one. Nothing is added when
 *   the response has no `annualUsageLimitHours` field at all.
 * Lines can still repeat across cards, so callers key each row by its index, never its text.
 */
export function balanceCardPolicyLines(
  lines: string[],
  options: {tracksBalance: boolean; annualUsageLimitHours?: number | null},
): string[] {
  const unique = lines.filter((line, index) => lines.indexOf(line) === index)
  const {tracksBalance, annualUsageLimitHours} = options
  if (
    tracksBalance ||
    annualUsageLimitHours === undefined ||
    unique.some((line) => line.startsWith(ANNUAL_USAGE_LIMIT_LABEL))
  ) {
    return unique
  }
  return [...unique, annualUsageLimitLine(annualUsageLimitHours)]
}

/**
 * The meta line for an Accrual card's usage under the policy it replaced (STFFLG-4355), shared by
 * the admin Assignment → Time Off card and the candidate My Info → Time Off card:
 * "Used this year under earlier policies: 45.00 hrs", the value bolded like every meta line.
 *
 * The figure is the msp's `carriedUsedHours`: this year's Approved hours in the card's category
 * whose usage was charged to another policy, usually the one this policy replaced. Those hours are
 * not in the card's "used this year" figure, but they still come off its available balance
 * (STFFLG-4311 Q12(a)), and this line says where they went.
 *
 * Null, so the card shows no line, when there is nothing to report: 0, null (a Flexible or Manual
 * card) or undefined (an msp before STFFLG-4355).
 */
export function carriedUsageLine(
  carriedUsedHours?: number | null,
): {label: string; value: string} | null {
  if (typeof carriedUsedHours !== 'number' || !(carriedUsedHours > 0)) {
    return null
  }
  return {
    label: 'Used this year under earlier policies:',
    value: `${carriedUsedHours.toFixed(2)} hrs`,
  }
}

/** The year-scoped figures the msp sends on a Flexible or Manual balance card (STFFLG-4313). */
export interface AnnualUsageFigureInput {
  /** L: the policy's annual usage limit in hours; null = no limit. */
  annualUsageLimitHours?: number | null
  /** U: hours of Approved requests on this policy that start in the current UTC year. */
  annualUsedHours?: number | null
  /** P: hours of Pending requests on this policy that start in the current UTC year. */
  annualPendingHours?: number | null
  /** R: max(0, L − U); pending hours are not subtracted. Null when there is no limit. */
  annualRemainingHours?: number | null
}

/** What a Flexible or Manual balance card shows above its divider. */
export interface AnnualUsageFigures {
  /** The headline figure (R to 2 decimals), or null for the '—' of a policy with no limit. */
  value: string | null
  /** The caption beside the headline. */
  caption: string
  /** The line under the headline. */
  usedLine: string
  /**
   * The balance bar's fill, 0–100: the hours left of the limit (STFFLG-4356). Null with no limit,
   * when the card draws no bar.
   */
  barPercent: number | null
}

/**
 * A Flexible or Manual card's figures (STFFLG-4313), from the msp's year-scoped fields. The year is
 * a request's startDate UTC year, the same year the msp's annual limit check counts; U, P and R
 * are defined on `AnnualUsageFigureInput`.
 * - With a limit: "R hours available", then "U of L hours used this year", plus " · P pending"
 *   when P > 0, and a bar at round(R / L · 100), the hours left of the limit (STFFLG-4356; it was
 *   the share used, U / L, which read full on a limit of 0). An admin request can still pass the
 *   limit on an msp before STFFLG-4360, so U can pass L: R and the bar stay at 0. A limit of 0
 *   reads 0.
 * - With no limit: '—' with "no annual limit", then "U hours used this year" (plus pending), and
 *   no bar (`barPercent` null).
 * Returns null when the msp sent no annual figures (an Accrual card, or an msp before
 * STFFLG-4313); the card then keeps its placeholder text and draws no bar.
 */
export function annualUsageFigures(
  card: AnnualUsageFigureInput,
  unit = 'hours',
): AnnualUsageFigures | null {
  const used = card.annualUsedHours
  if (used === null || used === undefined) {
    return null
  }
  const pending = card.annualPendingHours ?? 0
  const pendingSuffix = pending > 0 ? ` · ${pending.toFixed(2)} pending` : ''
  const limit = card.annualUsageLimitHours
  if (limit === null || limit === undefined) {
    return {
      value: null,
      caption: 'no annual limit',
      usedLine: `${used.toFixed(2)} ${unit} used this year${pendingSuffix}`,
      barPercent: null,
    }
  }
  const remaining = Math.max(0, card.annualRemainingHours ?? limit - used)
  return {
    value: remaining.toFixed(2),
    caption: `${unit} available`,
    usedLine: `${used.toFixed(2)} of ${limit.toFixed(2)} ${unit} used this year${pendingSuffix}`,
    barPercent: limit > 0 ? Math.min(100, Math.round((remaining / limit) * 100)) : 0,
  }
}

/** The card fields an Accrual card's balance bar reads (STFFLG-4356). */
export interface BalanceBarInput {
  /** A: the hours available; null or absent on a card that tracks no balance (Flexible/Manual). */
  availableHours?: number | null
  /** U: hours used this year under the card's policy (STFFLG-4355). */
  usedHours?: number | null
  /**
   * The msp's cap in hours at the candidate's current milestone (`TimeOffBalanceCardDto.capHours`,
   * mspplatform caa707ca5): above 0, else null, and absent from an older msp. The card's "Current
   * cap" text line can name another figure after a milestone with its own cap; the bar uses this.
   */
  capHours?: number | null
}

/** The card's cap when it has a usable one (a finite number above 0), else null. */
const effectiveCapHours = (card: BalanceBarInput): number | null => {
  const cap = card.capHours
  return typeof cap === 'number' && Number.isFinite(cap) && cap > 0 ? cap : null
}

/**
 * An Accrual card's balance bar fill, 0–100 (STFFLG-4356): how full the balance is, never the
 * share used. The bar used to paint the API's `usagePercent` (U / (A + U)), so it read full
 * whenever the balance was 0 and anything had been used; `usagePercent` still means the share used
 * and is no longer painted.
 * - With a cap (`capHours`): A / cap, filling as hours accrue toward it. A manual ledger
 *   adjustment is added after the cap, so A can pass it and the fill stops at 100.
 * - With no cap (null, or absent from an older msp): the share still available, A / (A + U), with
 *   a U below 0 or not a number read as 0 (SHR-4336 parity).
 * - 0 when A is 0 or below (the msp floors a scheduled balance at 0; a Per Hours Worked balance
 *   can be signed). At least 1 when A is above 0, so a positive balance never reads empty.
 * - null when the card tracks no balance (A null or absent): the card draws no bar.
 */
export function balanceBarPercent(card: BalanceBarInput): number | null {
  const available = card.availableHours
  if (available === null || available === undefined) {
    return null
  }
  if (!(available > 0)) {
    return 0
  }
  const used = typeof card.usedHours === 'number' && card.usedHours > 0 ? card.usedHours : 0
  const denominator = effectiveCapHours(card) ?? available + used
  return Math.max(1, Math.min(100, Math.round((available / denominator) * 100)))
}

/**
 * The accessible name of a balance card's bar (`role="progressbar"`, STFFLG-4356): the card's
 * name, then what the fill measures, the way `balanceBarPercent` and `annualUsageFigures` fill it:
 * - an Accrual card with a cap: "Vacation: hours available of the 48-hour cap";
 * - an Accrual card with no cap: "Vacation: share of hours still available";
 * - a limited Flexible or Manual card (no balance): "Sick Leave: hours left of the 2026 annual
 *   limit", the current UTC year the msp's annual figures count.
 */
export function balanceBarLabel(
  name: string,
  card: BalanceBarInput,
  today: Date = new Date(),
): string {
  if (card.availableHours === null || card.availableHours === undefined) {
    return `${name}: hours left of the ${today.getUTCFullYear()} annual limit`
  }
  const cap = effectiveCapHours(card)
  if (cap !== null) {
    return `${name}: hours available of the ${Number(cap.toFixed(2))}-hour cap`
  }
  return `${name}: share of hours still available`
}
